// Capa única de acceso a datos para las páginas — offline-first.
// Lecturas: Dexie primero (instantáneo, funciona offline), refresco en
// background vía el motor de sync cuando hay señal (stale-while-revalidate).
// Escrituras: se aplican primero en Dexie (optimista) y se encolan con
// queueOp(); si hay señal se dispara syncNow() sin bloquear la UI.
import { useEffect, useMemo } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import { db, uuid, nowIso, getMeta, META } from "@/lib/db";
import { queueOp, syncNow } from "@/lib/sync";
import { deriveCategory } from "@/lib/utils";

function isOnline() {
  return typeof navigator === "undefined" || navigator.onLine;
}

function stripLocal(doc) {
  const { _sync_state, _local_updated_at, ...rest } = doc;
  return rest;
}

function maybeSync() {
  if (isOnline()) syncNow();
}

// Dispara un pull/push del motor de sync al montar una pantalla (si hay señal).
// Las tablas Dexie son reactivas vía useLiveQuery, así que en cuanto el pull
// escribe datos nuevos, la UI se actualiza sola.
export function useBackgroundRefresh() {
  useEffect(() => { maybeSync(); }, []);
}

function last5(s) {
  const digits = String(s || "").replace(/\D/g, "");
  return digits ? digits.slice(-5) : null;
}

// ---------------------------------------------------------------------------
// FARMS
// ---------------------------------------------------------------------------
export function useFarms() {
  useBackgroundRefresh();
  const farms = useLiveQuery(() => db.farms.filter((f) => !f.deleted_at).toArray(), []);
  return useMemo(() => (farms || []).sort((a, b) => (a.name || "").localeCompare(b.name || "")), [farms]);
}

export async function createFarmLocal(payload) {
  const id = uuid();
  const now = nowIso();
  const doc = {
    id, name: payload.name, location: payload.location || null, handler_id: payload.handler_id || null,
    created_at: now, updated_at: now, deleted_at: null,
    _sync_state: "pending_create", _local_updated_at: now,
  };
  await db.farms.put(doc);
  await queueOp({ entity: "farm", operation: "create", entity_id: id, payload: stripLocal(doc) });
  maybeSync();
  return doc;
}

export async function updateFarmLocal(id, payload) {
  const now = nowIso();
  const patch = { name: payload.name, location: payload.location || null, handler_id: payload.handler_id || null, updated_at: now };
  await db.farms.update(id, { ...patch, _sync_state: "pending_update", _local_updated_at: now });
  await queueOp({ entity: "farm", operation: "update", entity_id: id, payload: patch });
  maybeSync();
  return db.farms.get(id);
}

export async function deleteFarmLocal(id) {
  const now = nowIso();
  await db.farms.update(id, { deleted_at: now, updated_at: now, _sync_state: "pending_update", _local_updated_at: now });
  await queueOp({ entity: "farm", operation: "delete", entity_id: id, payload: {} });
  maybeSync();
}

// ---------------------------------------------------------------------------
// LOTS
// ---------------------------------------------------------------------------
export function useLots(farmId) {
  useBackgroundRefresh();
  const lots = useLiveQuery(
    () => (farmId ? db.lots.where("farm_id").equals(farmId).filter((l) => !l.deleted_at).toArray() : []),
    [farmId]
  );
  return lots || [];
}

export async function createLotLocal(farmId, payload) {
  const id = uuid();
  const now = nowIso();
  const doc = {
    id, farm_id: farmId, name: payload.name, description: payload.description || null,
    created_at: now, updated_at: now, deleted_at: null,
    _sync_state: "pending_create", _local_updated_at: now,
  };
  await db.lots.put(doc);
  await queueOp({ entity: "lot", operation: "create", entity_id: id, payload: stripLocal(doc) });
  maybeSync();
  return doc;
}

export async function deleteLotLocal(id) {
  const now = nowIso();
  await db.lots.update(id, { deleted_at: now, updated_at: now, _sync_state: "pending_update", _local_updated_at: now });
  await queueOp({ entity: "lot", operation: "delete", entity_id: id, payload: {} });
  maybeSync();
}

// ---------------------------------------------------------------------------
// ANIMALS — lectura con filtros (replica list_animals del backend)
// ---------------------------------------------------------------------------
export function useAnimals(filters = {}) {
  useBackgroundRefresh();
  const key = JSON.stringify(filters || {});
  const animals = useLiveQuery(async () => {
    let arr = await db.animals.filter((a) => !a.deleted_at).toArray();
    if (filters.farm_id) arr = arr.filter((a) => a.farm_id === filters.farm_id);
    if (filters.assigned_farm_ids && filters.assigned_farm_ids.length > 0) arr = arr.filter((a) => filters.assigned_farm_ids.includes(a.farm_id));
    if (filters.lot_id) arr = arr.filter((a) => a.lot_id === filters.lot_id);
    if (filters.category) arr = arr.filter((a) => a.category === filters.category);
    if (filters.sex) arr = arr.filter((a) => a.sex === filters.sex);
    if (filters.is_sick) arr = arr.filter((a) => !!a.is_sick);
    else if (filters.is_elite) arr = arr.filter((a) => !!a.is_elite); // Fix P
    else if (filters.is_good_mother) arr = arr.filter((a) => !!a.is_good_mother); // Fix Q
    else if (filters.needs_detail) arr = arr.filter((a) => !!a.needs_detail);
    else if (filters.status) arr = arr.filter((a) => (a.status || "active") === filters.status);
    if (filters.q) {
      const q = filters.q.toLowerCase();
      arr = arr.filter((a) =>
        (a.chip_code || "").toLowerCase().includes(q) ||
        (a.chips || []).some((c) => (c.rfid || "").toLowerCase().includes(q)) ||
        (a.lomo_number || "").toLowerCase().includes(q) ||
        (a.ear_tattoo || "").toLowerCase().includes(q)
      );
    }
    arr.sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || ""));
    if (filters.limit) arr = arr.slice(0, filters.limit);
    return arr;
  }, [key]);
  return animals || [];
}

export function useAnimal(id) {
  useBackgroundRefresh();
  return useLiveQuery(() => (id ? db.animals.get(id) : null), [id]);
}

export async function findAnimalByChipLocal(chip) {
  if (!chip) return null;
  const code = String(chip).trim();
  const all = await db.animals.filter((a) => !a.deleted_at).toArray();
  return all.find((a) => a.chip_code === code || (a.chips || []).some((c) => c.rfid === code)) || null;
}

export async function findAnimalByLomoLocal(lomo) {
  if (!lomo) return null;
  const code = String(lomo).trim();
  if (!code) return null;
  const all = await db.animals.filter((a) => !a.deleted_at).toArray();
  return all.find((a) => a.lomo_number === code) || null;
}

// Re-encola un animal local completo hacia el server. Se usa como reparación
// automática cuando /actions/submit devuelve 404 pese a que el animal existe
// en Dexie (una sincronización que se perdió alguna vez): el push es upsert
// por id en el backend, así que esto lo re-crea sin duplicar nada.
export async function resyncAnimalLocal(animalId) {
  const doc = await db.animals.get(animalId);
  if (!doc) return null;
  const now = nowIso();
  await db.animals.update(animalId, { _sync_state: "pending_update", _local_updated_at: now });
  await queueOp({ entity: "animal", operation: "update", entity_id: animalId, payload: stripLocal(doc) });
  maybeSync();
  return doc;
}

function ensureChipList(animal) {
  if (Array.isArray(animal.chips) && animal.chips.length) return animal.chips;
  if (!animal.chip_code) return [];
  return [{
    rfid: animal.chip_code, status: "active", is_primary: true, reason: "original",
    linked_at: animal.created_at || nowIso(), linked_by: animal.created_by || null, notes: null,
  }];
}

async function isChipTaken(chip, excludeId) {
  const all = await db.animals.filter((a) => !a.deleted_at).toArray();
  return all.some((a) => {
    if (a.id === excludeId) return false;
    if (a.chip_code === chip) return true;
    return (a.chips || []).some((c) => c.rfid === chip && ["active", "backup"].includes(c.status));
  });
}

export async function createAnimalLocal(payload) {
  const chip = (payload.chip_code || "").trim();
  if (await isChipTaken(chip)) {
    throw { userMessage: `El chip ${chip} ya está vinculado a otro animal` };
  }
  const id = uuid();
  const now = nowIso();
  const category = payload.category || deriveCategory(payload.sex, payload.birth_date);
  const doc = {
    id,
    farm_id: payload.farm_id,
    lot_id: payload.lot_id || null,
    chip_code: chip,
    chips: [{ rfid: chip, status: "active", is_primary: true, reason: "original", linked_at: now, linked_by: null, notes: null }],
    lomo_number: payload.lomo_number || null,
    ear_tattoo: payload.ear_tattoo || null,
    ear_tattoo_right: payload.ear_tattoo_right || null,
    ear_tattoo_left: payload.ear_tattoo_left || null,
    weight_kg: payload.weight_kg ?? null,
    birth_date: payload.birth_date || null,
    sex: payload.sex || "female",
    breed: payload.breed || null,
    category,
    status: payload.status || "active",
    observations: payload.observations || null,
    photo_url: payload.photo_url || null,
    mother_id: payload.mother_id || null,
    mother_chip: payload.mother_chip || null,
    mother_lomo: payload.mother_lomo || null,
    origin: payload.origin || "manual",
    iron_brand: payload.iron_brand || null,
    iron_brand_drawing: payload.iron_brand_drawing || null,
    is_sick: payload.is_sick || false,
    needs_detail: false,
    created_at: now, updated_at: now, deleted_at: null,
    _sync_state: "pending_create", _local_updated_at: now,
  };
  await db.animals.put(doc);
  await queueOp({ entity: "animal", operation: "create", entity_id: id, payload: stripLocal(doc) });
  await createEventLocal(id, { type: "birth", date: doc.birth_date || now, payload: { note: "Alta en el sistema" } }, { silentSync: true });
  maybeSync();
  return doc;
}

export async function updateAnimalLocal(id, payload) {
  const existing = await db.animals.get(id);
  if (!existing) throw { userMessage: "Animal no encontrado" };
  const now = nowIso();
  const category = payload.category || deriveCategory(payload.sex, payload.birth_date);
  const patch = {
    farm_id: payload.farm_id, lot_id: payload.lot_id || null,
    lomo_number: payload.lomo_number || null, ear_tattoo: payload.ear_tattoo || null,
    ear_tattoo_right: payload.ear_tattoo_right || null, ear_tattoo_left: payload.ear_tattoo_left || null,
    weight_kg: payload.weight_kg ?? null, birth_date: payload.birth_date || null,
    sex: payload.sex, breed: payload.breed || null, category, status: payload.status || existing.status,
    observations: payload.observations || null, iron_brand: payload.iron_brand || null,
    iron_brand_drawing: payload.iron_brand_drawing || null,
    // Fix C (2026-08-24): la foto del hierro debe viajar igual que el dibujo.
    iron_brand_photo: payload.iron_brand_photo || null, needs_detail: false,
    mother_id: payload.mother_id ?? existing.mother_id ?? null,
    mother_chip: payload.mother_chip ?? existing.mother_chip ?? null,
    mother_lomo: payload.mother_lomo ?? existing.mother_lomo ?? null,
    updated_at: now,
  };
  // Fix N (2026-10-06): si este equipo todavía no bajó la foto del hierro (ahora llega
  // aparte), no se manda "sin foto": eso la borraría del servidor. Solo se borra si
  // estaba cargada en pantalla y el usuario la quitó.
  if (!patch.iron_brand_photo && !existing.iron_brand_photo && existing.has_iron_brand_photo) delete patch.iron_brand_photo;
  await db.animals.update(id, { ...patch, _sync_state: "pending_update", _local_updated_at: now });
  await queueOp({ entity: "animal", operation: "update", entity_id: id, payload: patch });
  maybeSync();
  return db.animals.get(id);
}

export async function deleteAnimalLocal(id) {
  const now = nowIso();
  await db.animals.update(id, { deleted_at: now, updated_at: now, _sync_state: "pending_update", _local_updated_at: now });
  await queueOp({ entity: "animal", operation: "delete", entity_id: id, payload: {} });
  maybeSync();
}

// Fix A (2026-08-24): tercer parámetro opcional healthNote — al marcar como
// sano se limpia la nota; al marcar enfermo se guarda el texto si vino. Los
// llamadores que pasan solo 2 argumentos mantienen el comportamiento anterior.
export async function updateHealthFlagLocal(id, is_sick, healthNote) {
  const now = nowIso();
  const health_note = is_sick ? (healthNote ?? null) : null;
  await db.animals.update(id, { is_sick, health_note, updated_at: now, _sync_state: "pending_update", _local_updated_at: now });
  await queueOp({ entity: "animal", operation: "update", entity_id: id, payload: { is_sick, health_note, updated_at: now } });
  maybeSync();
  return db.animals.get(id);
}

// Fix Q (2026-10-21): marca "buena habilidad materna" (para elegir madres). Igual que élite.
export async function updateGoodMotherFlagLocal(id, value) {
  const now = nowIso();
  await db.animals.update(id, { is_good_mother: !!value, updated_at: now, _sync_state: "pending_update", _local_updated_at: now });
  await queueOp({ entity: "animal", operation: "update", entity_id: id, payload: { is_good_mother: !!value, updated_at: now } });
  maybeSync();
  return db.animals.get(id);
}

// Fix Q (2026-10-21): crías de una madre, para mostrar sus partos en el Historial. Se
// deriva de las crías cargadas (por mother_id o por el chip de la madre), así aparecen
// también los partos registrados antes de este cambio, sin migrar nada.
export function useCalvesOf(animal) {
  useBackgroundRefresh();
  const chips = animal ? [animal.chip_code, ...(animal.chips || []).map((c) => c.rfid)].filter(Boolean) : [];
  const key = animal ? `${animal.id}|${chips.join(",")}` : "";
  const calves = useLiveQuery(async () => {
    if (!animal) return [];
    const set = new Set(chips);
    return db.animals
      .filter((a) => !a.deleted_at && a.id !== animal.id && (a.mother_id === animal.id || (!!a.mother_chip && set.has(a.mother_chip))))
      .toArray();
  }, [key]);
  return calves || [];
}

// Fix Q (2026-10-21): animales activos de un lote (Cambio de lote → "lote de origen").
export async function listAnimalsInLotLocal(lotId) {
  if (!lotId) return [];
  return db.animals.filter((a) => !a.deleted_at && a.lot_id === lotId && (a.status || "active") === "active").toArray();
}

// Fix P (2026-10-06): marca "élite" del animal. Funciona sin señal, igual que la de salud.
export async function updateEliteFlagLocal(id, is_elite) {
  const now = nowIso();
  await db.animals.update(id, { is_elite: !!is_elite, updated_at: now, _sync_state: "pending_update", _local_updated_at: now });
  await queueOp({ entity: "animal", operation: "update", entity_id: id, payload: { is_elite: !!is_elite, updated_at: now } });
  maybeSync();
  return db.animals.get(id);
}

// ---------------------------------------------------------------------------
// Papelera (Tanda 2) — el borrado/restauración masiva se resuelve siempre
// contra el servidor (requiere conexión: coordina el freno de seguridad y el
// backup previo). Estas funciones solo reflejan el resultado ya confirmado
// en el espejo local, para que la UI no tenga que esperar el próximo pull.
// ---------------------------------------------------------------------------
export async function applyBulkDeleteLocal(ids) {
  const now = nowIso();
  await db.animals.bulkUpdate(ids.map((id) => ({
    key: id,
    changes: { deleted_at: now, updated_at: now, chip_code: null, _sync_state: "synced", _local_updated_at: now },
  })));
}

export async function applyRestoredAnimalLocal(animal) {
  await db.animals.put({ ...animal, _sync_state: "synced", _local_updated_at: animal.updated_at || nowIso() });
}

// ---------------------------------------------------------------------------
// Registro de nacimiento — replica /animals/birth localmente.
// ---------------------------------------------------------------------------
export async function registerBirthLocal(data) {
  const calfChip = (data.calf_chip_code || "").trim();
  if (await isChipTaken(calfChip)) {
    throw { userMessage: `El chip ${calfChip} ya está vinculado a otro animal` };
  }
  let mother = null;
  const motherChip = (data.mother_chip || "").trim() || null;
  const motherLomo = (data.mother_lomo || "").trim() || null;
  if (motherChip || motherLomo) {
    const all = await db.animals.filter((a) => !a.deleted_at).toArray();
    mother = all.find((a) =>
      (motherChip && (a.chip_code === motherChip || (a.chips || []).some((c) => c.rfid === motherChip))) ||
      (motherLomo && a.lomo_number === motherLomo)
    ) || null;
  }
  const resolvedMotherChip = motherChip || mother?.chip_code || null;
  const resolvedMotherLomo = motherLomo || mother?.lomo_number || null;
  const earRight = data.ear_tattoo_right || last5(calfChip);
  const earLeft = data.ear_tattoo_left || last5(resolvedMotherChip);
  const category = deriveCategory(data.sex, data.birth_date);
  const id = uuid();
  const now = nowIso();
  const doc = {
    id, farm_id: data.farm_id, lot_id: data.lot_id || null,
    chip_code: calfChip,
    chips: [{ rfid: calfChip, status: "active", is_primary: true, reason: "original", linked_at: now, linked_by: null, notes: null }],
    lomo_number: earRight, ear_tattoo: earRight, ear_tattoo_right: earRight, ear_tattoo_left: earLeft,
    weight_kg: data.weight_kg ?? null, birth_date: data.birth_date, sex: data.sex, breed: data.breed || null,
    category, status: "active", observations: data.observations || null, photo_url: null,
    mother_id: mother?.id || null, mother_chip: resolvedMotherChip, mother_lomo: resolvedMotherLomo,
    origin: "birth", needs_detail: false,
    created_at: now, updated_at: now, deleted_at: null,
    _sync_state: "pending_create", _local_updated_at: now,
  };
  await db.animals.put(doc);
  await queueOp({ entity: "animal", operation: "create", entity_id: id, payload: stripLocal(doc) });
  await createEventLocal(id, {
    type: "birth", date: data.birth_date,
    payload: {
      kind: "birth_registration", farm_id: data.farm_id, weight_kg: data.weight_kg,
      ear_tattoo_right: earRight, ear_tattoo_left: earLeft,
      mother_id: doc.mother_id, mother_chip: resolvedMotherChip, mother_lomo: resolvedMotherLomo,
      note: "Registro de nacimiento",
    },
  }, { silentSync: true });
  maybeSync();
  return doc;
}

// ---------------------------------------------------------------------------
// Inventario inicial — replica /animals/bulk-initial localmente.
// ---------------------------------------------------------------------------
export async function bulkInitialLocal({ farm_id, lot_id, sex, breed, category, chips }) {
  const batchId = uuid();
  const now = nowIso();
  const created = [];
  const skipped = [];
  const seen = new Set();
  const existingAll = await db.animals.filter((a) => !a.deleted_at).toArray();
  const takenChips = new Set();
  existingAll.forEach((a) => {
    if (a.chip_code) takenChips.add(a.chip_code);
    (a.chips || []).forEach((c) => { if (["active", "backup"].includes(c.status)) takenChips.add(c.rfid); });
  });

  for (const raw of chips) {
    const chip = String(raw).replace(/\D/g, "");
    if (!chip || chip.length < 6) { skipped.push({ chip: String(raw), reason: "invalid" }); continue; }
    if (seen.has(chip)) { skipped.push({ chip, reason: "duplicate_in_batch" }); continue; }
    seen.add(chip);
    if (takenChips.has(chip)) { skipped.push({ chip, reason: "exists" }); continue; }
    takenChips.add(chip);
    const id = uuid();
    const doc = {
      id, farm_id, lot_id: lot_id || null, chip_code: chip,
      chips: [{ rfid: chip, status: "active", is_primary: true, reason: "original", linked_at: now, linked_by: null, notes: null }],
      lomo_number: null, ear_tattoo: null, ear_tattoo_right: null, ear_tattoo_left: null,
      weight_kg: null, birth_date: null, sex, breed: breed || null, category: category || null,
      status: "active", observations: null, photo_url: null,
      mother_id: null, mother_chip: null, mother_lomo: null,
      origin: "initial", needs_detail: true, initial_batch_id: batchId,
      created_at: now, updated_at: now, deleted_at: null,
      _sync_state: "pending_create", _local_updated_at: now,
    };
    await db.animals.put(doc);
    await queueOp({ entity: "animal", operation: "create", entity_id: id, payload: stripLocal(doc) });
    await createEventLocal(id, {
      type: "birth", date: now,
      payload: { note: "Alta — inventario inicial (carga masiva)", batch_id: batchId },
    }, { silentSync: true });
    created.push({ id, chip_code: chip });
  }
  maybeSync();
  return {
    batch_id: batchId, farm_id, created, skipped,
    total_created: created.length, total_skipped: skipped.length,
  };
}

// ---------------------------------------------------------------------------
// EVENTS
// ---------------------------------------------------------------------------
export function useEvents(animalId) {
  useBackgroundRefresh();
  const events = useLiveQuery(
    () => (animalId ? db.events.where("animal_id").equals(animalId).filter((e) => !e.deleted_at).toArray() : []),
    [animalId]
  );
  return useMemo(() => (events || []).sort((a, b) => (b.date || "").localeCompare(a.date || "")), [events]);
}

export async function createEventLocal(animalId, data, opts = {}) {
  const id = uuid();
  const now = nowIso();
  const doc = {
    id, animal_id: animalId, type: data.type, date: data.date || now, payload: data.payload || {},
    created_at: now, updated_at: now,
    _sync_state: "pending_create", _local_updated_at: now,
  };
  await db.events.put(doc);
  await queueOp({ entity: "event", operation: "create", entity_id: id, payload: stripLocal(doc) });
  // Reflejar efectos secundarios simples del backend (death/sale/treatment) en el
  // espejo local para que la UI se vea consistente antes de sincronizar.
  if (data.type === "death" || data.type === "sale") {
    await db.animals.update(animalId, {
      status: data.type === "death" ? "dead" : "sold", updated_at: now,
      _sync_state: "pending_update", _local_updated_at: now,
    });
    await queueOp({ entity: "animal", operation: "update", entity_id: animalId, payload: { status: data.type === "death" ? "dead" : "sold", updated_at: now } });
  }
  if (data.type === "treatment") {
    await db.animals.update(animalId, { is_sick: true, updated_at: now, _sync_state: "pending_update", _local_updated_at: now });
    await queueOp({ entity: "animal", operation: "update", entity_id: animalId, payload: { is_sick: true, updated_at: now } });
  }
  if (!opts.silentSync) maybeSync();
  return doc;
}

// ---------------------------------------------------------------------------
// CHIPS — vincular / actualizar / desvincular (opera sobre animal.chips)
// ---------------------------------------------------------------------------
async function persistChips(animalId, chips) {
  const primary = chips.find((c) => c.is_primary) || chips.find((c) => c.status === "active") || chips[0];
  const now = nowIso();
  const patch = { chips, updated_at: now };
  if (primary) patch.chip_code = primary.rfid;
  await db.animals.update(animalId, { ...patch, _sync_state: "pending_update", _local_updated_at: now });
  await queueOp({ entity: "animal", operation: "update", entity_id: animalId, payload: patch });
  maybeSync();
  return db.animals.get(animalId);
}

export async function linkChipLocal(animalId, { rfid, reason, make_primary, previous_status, notes }) {
  const animal = await db.animals.get(animalId);
  if (!animal) throw { userMessage: "Animal no encontrado" };
  const chips = ensureChipList(animal);
  const code = (rfid || "").trim();
  if (!code) throw { userMessage: "El RFID no puede estar vacío" };

  const existingHere = chips.find((c) => c.rfid === code);
  if (existingHere) {
    existingHere.status = "active";
    if (make_primary) {
      chips.forEach((c) => { c.is_primary = false; });
      existingHere.is_primary = true;
      existingHere.reason = "recovered";
    }
    return persistChips(animalId, chips);
  }

  if (await isChipTaken(code, animalId)) {
    throw { userMessage: `El chip ${code} ya está vinculado a otro animal` };
  }
  if (previous_status) {
    chips.forEach((c) => { if (c.is_primary) c.status = previous_status; });
  }
  if (make_primary) chips.forEach((c) => { c.is_primary = false; });
  chips.push({
    rfid: code, status: "active", is_primary: !!make_primary, reason: reason || "replacement",
    linked_at: nowIso(), linked_by: null, notes: notes || null,
  });
  return persistChips(animalId, chips);
}

export async function updateChipLocal(animalId, rfid, { status, make_primary, notes }) {
  const animal = await db.animals.get(animalId);
  if (!animal) throw { userMessage: "Animal no encontrado" };
  const chips = ensureChipList(animal);
  const target = chips.find((c) => c.rfid === rfid);
  if (!target) throw { userMessage: "Chip no vinculado a este animal" };

  if (status) {
    if (["damaged", "lost"].includes(status) && target.is_primary) {
      const otherActive = chips.find((c) => c.rfid !== rfid && c.status === "active");
      if (!otherActive) throw { userMessage: "Vinculá un chip de reemplazo antes de marcar como dañado/perdido el principal" };
      chips.forEach((c) => { c.is_primary = (c === otherActive); });
    }
    target.status = status;
  }
  if (make_primary === true) {
    if (target.status !== "active") target.status = "active";
    chips.forEach((c) => { c.is_primary = false; });
    target.is_primary = true;
  }
  if (notes !== undefined && notes !== null) target.notes = notes;
  return persistChips(animalId, chips);
}

export async function unlinkChipLocal(animalId, rfid) {
  const animal = await db.animals.get(animalId);
  if (!animal) throw { userMessage: "Animal no encontrado" };
  let chips = ensureChipList(animal);
  const target = chips.find((c) => c.rfid === rfid);
  if (!target) throw { userMessage: "Chip no vinculado a este animal" };
  if (chips.length === 1) throw { userMessage: "No podés desvincular el único chip del animal. Vinculá otro antes." };
  if (target.is_primary) {
    const others = chips.filter((c) => c.rfid !== rfid);
    const promote = others.find((c) => c.status === "active") || others[0];
    others.forEach((c) => { c.is_primary = (c === promote); });
    if (promote.status !== "active") promote.status = "active";
    chips = others;
  } else {
    chips = chips.filter((c) => c.rfid !== rfid);
  }
  return persistChips(animalId, chips);
}

// ---------------------------------------------------------------------------
// INVENTORIES
// ---------------------------------------------------------------------------
export function useInventories(farmId) {
  useBackgroundRefresh();
  const invs = useLiveQuery(
    () => (farmId ? db.inventories.where("farm_id").equals(farmId).toArray() : db.inventories.toArray()),
    [farmId]
  );
  return useMemo(() => (invs || []).sort((a, b) => (b.started_at || "").localeCompare(a.started_at || "")), [invs]);
}

export function useInventory(id) {
  useBackgroundRefresh();
  return useLiveQuery(() => (id ? db.inventories.get(id) : null), [id]);
}

export async function createInventoryLocal({ farm_id, lot_id, name }) {
  let animals = await db.animals.where("farm_id").equals(farm_id).filter((a) => !a.deleted_at && a.status === "active").toArray();
  if (lot_id) animals = animals.filter((a) => a.lot_id === lot_id);
  const id = uuid();
  const now = nowIso();
  const doc = {
    id, farm_id, lot_id: lot_id || null, name: name || null,
    started_at: now, finished_at: null, started_by: null,
    expected_animal_ids: animals.map((a) => a.id),
    scanned_animal_ids: [], missing_animal_ids: [], unknown_chips: [], scans_via_secondary: [],
    notes: null, status: "in_progress",
    created_at: now, updated_at: now,
    _sync_state: "pending_create", _local_updated_at: now,
  };
  await db.inventories.put(doc);
  await queueOp({ entity: "inventory", operation: "create", entity_id: id, payload: stripLocal(doc) });
  maybeSync();
  return doc;
}

export async function scanChipLocal(invId, chipCode) {
  const inv = await db.inventories.get(invId);
  if (!inv) throw { userMessage: "Inventario no encontrado" };
  const animal = await findAnimalByChipLocal(chipCode);
  const now = nowIso();
  if (animal) {
    const chips = ensureChipList(animal);
    const matched = chips.find((c) => c.rfid === chipCode) || (animal.chip_code === chipCode ? { rfid: chipCode, status: "active", is_primary: true } : null);
    const chipStatus = matched?.status || "active";
    const isPrimary = !!matched?.is_primary;

    const scannedSet = new Set(inv.scanned_animal_ids || []);
    const secondarySet = new Set(inv.scans_via_secondary || []);
    scannedSet.add(animal.id);
    if (!isPrimary) secondarySet.add(animal.id);
    await db.inventory_scans.put({
      id: uuid(), inventory_id: invId, animal_id: animal.id, chip_code: chipCode,
      chip_status_at_scan: chipStatus, via_secondary_chip: !isPrimary, scanned_at: now,
    });
    const patch = {
      scanned_animal_ids: Array.from(scannedSet), scans_via_secondary: Array.from(secondarySet),
      updated_at: now,
    };
    await db.inventories.update(invId, { ...patch, _sync_state: "pending_update", _local_updated_at: now });
    return { status: "present", animal, chip: matched, via_secondary_chip: !isPrimary, chip_status_at_scan: chipStatus };
  }
  const unknown = Array.from(new Set([...(inv.unknown_chips || []), chipCode]));
  await db.inventories.update(invId, { unknown_chips: unknown, updated_at: now, _sync_state: "pending_update", _local_updated_at: now });
  return { status: "unknown", chip_code: chipCode };
}

export async function finishInventoryLocal(invId, notes) {
  const inv = await db.inventories.get(invId);
  if (!inv) throw { userMessage: "Inventario no encontrado" };
  const scanned = new Set(inv.scanned_animal_ids || []);
  const missing = (inv.expected_animal_ids || []).filter((id) => !scanned.has(id));
  const now = nowIso();
  const patch = {
    missing_animal_ids: missing, finished_at: now, status: "finished", notes: notes || null, updated_at: now,
  };
  await db.inventories.update(invId, { ...patch, _sync_state: "pending_update", _local_updated_at: now });
  const fresh = await db.inventories.get(invId);
  const fullDoc = stripLocal(fresh);
  const scans = await db.inventory_scans.where("inventory_id").equals(invId).toArray();
  if (scans.length) fullDoc.scans = scans.map(({ id, ...s }) => s);
  await queueOp({ entity: "inventory", operation: "update", entity_id: invId, payload: fullDoc });
  maybeSync();
  return db.inventories.get(invId);
}

export async function cancelInventoryLocal(invId) {
  const now = nowIso();
  await db.inventories.update(invId, { status: "cancelled", finished_at: now, updated_at: now, _sync_state: "pending_update", _local_updated_at: now });
  await queueOp({ entity: "inventory", operation: "update", entity_id: invId, payload: { status: "cancelled", finished_at: now, updated_at: now } });
  maybeSync();
}

// ---------------------------------------------------------------------------
// DASHBOARD — agregado local (§5.F). Se calcula todo lo derivable de Dexie.
// ---------------------------------------------------------------------------
export function useDashboardLocal(farmId, assignedFarmIds) {
  useBackgroundRefresh();
  const animals = useAnimals({ farm_id: farmId || undefined, assigned_farm_ids: assignedFarmIds });
  const invs = useLiveQuery(() => db.inventories.toArray(), []);
  const farmsCount = useLiveQuery(() => db.farms.filter((f) => !f.deleted_at).count(), []);
  return useMemo(() => {
    const active = animals.filter((a) => (a.status || "active") === "active");
    const byCategory = {};
    active.forEach((a) => {
      const cat = a.category || "sin_categoria";
      byCategory[cat] = (byCategory[cat] || 0) + 1;
    });
    let lastInv = (invs || []).slice();
    if (farmId) lastInv = lastInv.filter((i) => i.farm_id === farmId);
    lastInv.sort((a, b) => (b.started_at || "").localeCompare(a.started_at || ""));
    return {
      totals: { animals_active: active.length, farms: farmsCount || 0, by_category: byCategory, by_status: {} },
      last_inventories: lastInv.slice(0, 5),
      last_events: [],
    };
  }, [animals, invs, farmId, farmsCount]);
}

// ---------------------------------------------------------------------------
// VENTAS Y DESPACHOS — derivado 100% de datos ya sincronizados (eventos de
// venta / sacrificio / salida de traslado + animales), igual que Nacimientos
// Previstos: funciona offline sin ninguna colección nueva en el backend. Las
// operaciones masivas (venta cría, venta matadero, salida de traslado)
// comparten `batch_id` — se agrupan en UN despacho por batch. El sacrificio
// en finca es de a un animal (sin batch_id): cada evento es su propio
// despacho.
// ---------------------------------------------------------------------------
export const DISPATCH_KINDS = ["sale_breeding", "sale_slaughterhouse", "slaughter", "transfer_out"];
export const DISPATCH_TYPE_LABEL = {
  sale_breeding: "Venta para cría",
  sale_slaughterhouse: "Venta a matadero",
  slaughter: "Sacrificio en finca",
  transfer_out: "Salida de traslado",
};

export function useDispatches(filters = {}) {
  useBackgroundRefresh();
  const events = useLiveQuery(
    () => db.events.where("type").anyOf(["sale", "slaughter", "transfer"]).filter((e) => !e.deleted_at).toArray(),
    []
  );
  const farms = useFarms();
  const animals = useAnimals({ assigned_farm_ids: filters.assignedFarmIds });
  return useMemo(() => {
    if (!events || !animals) return [];
    const animalsById = {};
    animals.forEach((a) => { animalsById[a.id] = a; });
    const farmsById = {};
    farms.forEach((f) => { farmsById[f.id] = f; });

    const groups = {};
    events.forEach((e) => {
      const kind = e.payload?.kind;
      if (!DISPATCH_KINDS.includes(kind)) return;
      const animal = animalsById[e.animal_id];
      if (!animal) return; // fuera de alcance (otra finca, papelera, sin sincronizar aún)
      const groupKey = e.batch_id || e.id;
      if (!groups[groupKey]) {
        groups[groupKey] = {
          id: groupKey, type: kind, date: e.date,
          farm_id: e.payload?.origin_farm_id || animal.farm_id,
          registered_by_name: e.payload?.registered_by_name || null,
          payload: e.payload || {},
          animals: [],
        };
      }
      groups[groupKey].animals.push({
        id: animal.id, chip_code: animal.chip_code, lomo_number: animal.lomo_number,
        ear_tattoo: animal.ear_tattoo, category: animal.category,
        weight_kg: e.payload?.weight_kg ?? null,
      });
    });

    let items = Object.values(groups).map((g) => {
      const totalWeight = g.animals.reduce((a, r) => a + (Number(r.weight_kg) || 0), 0);
      const count = g.animals.length;
      const destination = g.type === "sale_breeding"
        ? (g.payload.buyer || g.payload.destination || g.payload.dest_farm_name || "—")
        : g.type === "sale_slaughterhouse" ? (g.payload.matadero_name || "—")
        : g.type === "transfer_out" ? (farmsById[g.payload.to_farm_id]?.name || "—")
        : "Sacrificio en finca";
      const priceTotal = g.payload.price_total !== undefined && g.payload.price_total !== "" && g.payload.price_total !== null
        ? Number(g.payload.price_total) : null;
      const pricePerKg = g.payload.price_per_kg !== undefined && g.payload.price_per_kg !== "" && g.payload.price_per_kg !== null
        ? Number(g.payload.price_per_kg) : null;
      const amount = priceTotal != null ? priceTotal : (pricePerKg != null && totalWeight ? Math.round(pricePerKg * totalWeight * 100) / 100 : null);
      return {
        ...g,
        type_label: DISPATCH_TYPE_LABEL[g.type] || g.type,
        farm_name: farmsById[g.farm_id]?.name || "—",
        destination,
        animal_count: count,
        total_weight: Math.round(totalWeight * 100) / 100,
        avg_weight: count && totalWeight ? Math.round((totalWeight / count) * 100) / 100 : null,
        guide_number: g.payload.guide_number || null,
        guide_date: g.payload.guide_date || null,
        driver: g.payload.driver || null,
        driver_id: g.payload.driver_id || null,
        plate: g.payload.plate || null,
        price_per_kg: pricePerKg,
        price_total: priceTotal,
        amount,
      };
    });

    if (filters.farmId) items = items.filter((i) => i.farm_id === filters.farmId);
    if (filters.type) items = items.filter((i) => i.type === filters.type);
    if (filters.destinationQuery) {
      const q = filters.destinationQuery.toLowerCase();
      items = items.filter((i) => (i.destination || "").toLowerCase().includes(q));
    }
    if (filters.dateFrom) items = items.filter((i) => (i.date || "") >= filters.dateFrom);
    if (filters.dateTo) items = items.filter((i) => (i.date || "") <= `${filters.dateTo}T23:59:59`);
    items.sort((a, b) => (b.date || "").localeCompare(a.date || ""));
    return items;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, animals, farms, filters.farmId, filters.type, filters.destinationQuery, filters.dateFrom, filters.dateTo, JSON.stringify(filters.assignedFarmIds)]);
}

// ---------------------------------------------------------------------------
// NACIMIENTOS PREVISTOS — derivado 100% de datos ya sincronizados (eventos de
// palpación + animales), así funciona sin señal igual que el resto del
// tablero. Se toma la ÚLTIMA palpación de cada animal: si diagnosticó preñez
// y todavía no nació un becerro de esa madre después de esa fecha, se lista
// con la fecha estimada de parto (guardada por el backend en el evento). El
// "cierre del ciclo" es automático: en cuanto se registra el nacimiento (el
// animal nuevo queda en Dexie al instante, incluso offline), la previsión
// desaparece sola de la lista sin necesidad de ningún backend adicional.
export function usePredictedBirths(filters = {}) {
  useBackgroundRefresh();
  const view = filters.view || "pregnant";
  const events = useLiveQuery(
    () => db.events.where("type").equals("palpation").filter((e) => !e.deleted_at).toArray(),
    []
  );
  const animals = useAnimals({ assigned_farm_ids: filters.assignedFarmIds });
  return useMemo(() => {
    if (!events || !animals) return [];
    const animalsById = {};
    animals.forEach((a) => { animalsById[a.id] = a; });
    const latestByAnimal = {};
    events.forEach((e) => {
      if (!e.animal_id) return;
      const cur = latestByAnimal[e.animal_id];
      if (!cur || (e.date || "") >= (cur.date || "")) latestByAnimal[e.animal_id] = e;
    });
    let items = Object.values(latestByAnimal)
      .map((e) => {
        const diagnosis = e.payload?.diagnosis;
        const wanted = view === "empty" ? ["empty", "empty_cull"].includes(diagnosis) : diagnosis === "pregnant";
        if (!wanted) return null;
        const animal = animalsById[e.animal_id];
        if (!animal) return null;
        if (view !== "empty") {
          const hasBirth = animals.some((a) => a.mother_id === animal.id && (a.birth_date || a.created_at || "") >= (e.date || ""));
          if (hasBirth) return null;
        }
        return {
          id: e.id,
          animal,
          diagnosis,
          due_date: e.payload?.due_date || null,
          months: e.payload?.months ?? null,
          vet: e.payload?.vet || null,
          registered_by_name: e.payload?.registered_by_name || null,
          palpation_date: e.date,
        };
      })
      .filter(Boolean);
    if (filters.farmId) items = items.filter((i) => i.animal.farm_id === filters.farmId);
    if (filters.days) {
      const cutoffMs = Date.now() + filters.days * 86400000;
      items = items.filter((i) => !i.due_date || new Date(i.due_date).getTime() <= cutoffMs);
    }
    items.sort((a, b) => (a.due_date || "9999").localeCompare(b.due_date || "9999"));
    return items;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, animals, filters.farmId, filters.days, view, JSON.stringify(filters.assignedFarmIds)]);
}
